import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { TableDateDisplay } from '@/components/ui-v2/inputs/table/table-date-display';
import { TableDateInput } from '@/components/ui-v2/inputs/table/table-date-input';
import { useIsCellEditing } from '@/hooks/use-is-cell-editing';
import { useIsOfferLocked } from '@/hooks/use-is-offer-locked';
import { formatIsoDate, parseDateValue } from '@/lib/date-utils';
import {
  DealItem,
  DealItemWithVersions,
} from '@/lib/graphql/generated/graphql';
import { createSubRowSortingFn } from '@/lib/utils/table-filter-utils';
import { useSetColumnSort } from '@/states/column-sort.state';
import { Row } from '@tanstack/react-table';

import { ExtendedColumnDef } from '../table-products';
import { SortIcon } from './sort';
import { TableCellWithRecentlyModified } from './table-cell-with-recently-modified';

interface ColumnProps {
  handleCellClick: (id: string, field: keyof DealItem) => void;
  handleCellEdit: <K extends keyof DealItem>(
    id: string | undefined,
    field: K,
    value: DealItem[K],
    isLeafRow?: boolean
  ) => Promise<void>;
  handleKeyDown: React.KeyboardEventHandler<HTMLInputElement>;
  handleBlur: React.FocusEventHandler<HTMLInputElement>;
  exitEditing: () => void;
}

interface CouponExpirationDateCellContentProps {
  row: Row<DealItemWithVersions>;
  handleCellClick: ColumnProps['handleCellClick'];
  handleCellEdit: ColumnProps['handleCellEdit'];
  handleKeyDown: ColumnProps['handleKeyDown'];
  handleBlur: ColumnProps['handleBlur'];
  exitEditing: ColumnProps['exitEditing'];
}

function CouponExpirationDateCellContent({
  row,
  handleCellClick,
  handleCellEdit,
  handleKeyDown,
  handleBlur,
  exitEditing,
}: CouponExpirationDateCellContentProps) {
  const { i18n } = useTranslation();
  const isOfferLocked = useIsOfferLocked();
  const isEditing = useIsCellEditing(row, 'couponExpirationDate');
  const isParent = row.subRows.length > 1;

  const getValue = () => {
    const value = row.original.activeVersion.couponExpirationDate;
    if (typeof value === 'undefined' || value === null) return null;
    return value;
  };

  const getParentValue = () => {
    const subRowsValues: Date[] = [];
    row.subRows.forEach((subRow) => {
      const value = subRow.original.activeVersion.couponExpirationDate;
      if (typeof value !== 'undefined' && value !== null) {
        subRowsValues.push(value);
      }
    });
    if (subRowsValues.length === 0) return null;
    const firstValue = subRowsValues[0];
    const allSame = subRowsValues.every(
      (val) => val.getTime() === firstValue.getTime()
    );
    return allSame ? firstValue : null;
  };

  const expirationValue = isParent ? getParentValue() : getValue();
  const currentDate = expirationValue
    ? parseDateValue(expirationValue, i18n.language)
    : null;

  const handleLocalDateChange = (isoValue: string | null) => {
    const parsedDate = isoValue
      ? parseDateValue(isoValue, i18n.language)
      : null;
    exitEditing();
    if (
      parsedDate &&
      currentDate &&
      parsedDate.toISOString() === currentDate.toISOString()
    ) {
      return;
    }
    if (!parsedDate && !currentDate) {
      return;
    }
    void handleCellEdit(
      row.original.activeVersion.id,
      'couponExpirationDate',
      parsedDate as DealItem['couponExpirationDate'],
      !isParent
    );
  };

  return (
    <TableCellWithRecentlyModified
      productId={row.original.id}
      field="couponExpirationDate"
      isParent={isParent}
    >
      {(isRecentlyModified) => (
        <div className="h-full w-full">
          {isEditing ? (
            <TableDateInput
              value={
                expirationValue
                  ? (formatIsoDate(expirationValue) ?? null)
                  : null
              }
              onDateChange={handleLocalDateChange}
              autoFocus
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              disabled={isOfferLocked}
              language={i18n.language}
            />
          ) : (
            <TableDateDisplay
              value={expirationValue}
              isEmpty={expirationValue === null}
              isEditable={!isOfferLocked}
              isParent={isParent}
              isRecentlyModified={isRecentlyModified}
              onClick={() =>
                !isParent &&
                handleCellClick(
                  row.original.activeVersion.id,
                  'couponExpirationDate'
                )
              }
              language={i18n.language}
            />
          )}
        </div>
      )}
    </TableCellWithRecentlyModified>
  );
}

const useColumnDefCouponExpirationDate = ({
  handleBlur,
  handleCellClick,
  handleCellEdit,
  handleKeyDown,
  exitEditing,
}: ColumnProps): ExtendedColumnDef<DealItemWithVersions> => {
  const { t } = useTranslation();
  const { setColumnSort } = useSetColumnSort();

  const accessorKey: keyof DealItem = 'couponExpirationDate';

  return useMemo(
    () => ({
      accessorKey: accessorKey,
      name: 'Validité coupon',
      sortingFn: createSubRowSortingFn('couponExpirationDate', (row) => {
        const date = row.activeVersion?.couponExpirationDate;
        return date ? date.toISOString() : null;
      }),
      header: () => (
        <div
          className="flex items-center"
          onClick={() => setColumnSort(accessorKey)}
        >
          {t('kpi.coupon_expiration_date')}{' '}
          <SortIcon pointer="couponExpirationDate" />
        </div>
      ),
      cell: ({ row }) => (
        <CouponExpirationDateCellContent
          row={row}
          handleCellClick={handleCellClick}
          handleCellEdit={handleCellEdit}
          handleKeyDown={handleKeyDown}
          handleBlur={handleBlur}
          exitEditing={exitEditing}
        />
      ),
      size: 220,
    }),
    [
      t,
      setColumnSort,
      handleBlur,
      handleCellClick,
      handleCellEdit,
      handleKeyDown,
      exitEditing,
    ]
  );
};

export default useColumnDefCouponExpirationDate;
